import { Component, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Gantt, Willow } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/style.css";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import api, { fmtErr, isDriveEmployee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileImage, FileSpreadsheet, Flag, Lock, Plus, Save, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const uid = () => `wp-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
const addDays = (d, n) => { const dt = new Date(`${d}T00:00:00`); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };
const diffDays = (a, b) => Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000);
const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : null);

class GanttBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, retry: 0 };
  }
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  componentDidCatch() {
    this._t = setTimeout(() => this.setState((s) => ({ crashed: false, retry: s.retry + 1 })), 200);
  }
  componentWillUnmount() {
    clearTimeout(this._t);
  }
  render() {
    if (this.state.crashed) return <p className="py-10 text-center text-sm text-slate-400">…</p>;
    return <div key={this.state.retry} style={{ height: "100%" }} data-testid="wp-gantt-boundary">{this.props.children}</div>;
  }
}

function WpEditor({ program, onBack }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { project } = useProject();
  const { askConfirm, confirmDialog } = useConfirm();
  const [name, setName] = useState(program.name || "");
  const [meta, setMeta] = useState({ contract_no: "", revision: "", rev_date: "", prepared_by: "", approved_by: "", ...(program.meta || {}) });
  const [sections, setSections] = useState(() => (program.sections || []).map((s) => ({ id: s.id, title: s.title, tasks: (s.tasks || []).map((tk) => ({ ...tk })) })));
  const [exporting, setExporting] = useState(false);
  const [baseline, setBaseline] = useState(program.baseline || null);
  const ganttRef = useRef(null);
  const tableRef = useRef(null);
  const ganttWrapRef = useRef(null);
  const [ganttReady, setGanttReady] = useState(false);

  useEffect(() => {
    const el = ganttWrapRef.current;
    if (!el) return undefined;
    if (el.clientWidth > 0) { setGanttReady(true); return undefined; }
    const ro = new ResizeObserver(() => { if (el.clientWidth > 0) setGanttReady(true); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const allTasks = useMemo(() => sections.flatMap((s) => s.tasks.map((tk) => ({ ...tk, sectionTitle: s.title }))), [sections]);

  const addSection = () => setSections((ss) => [...ss, { id: uid(), title: "", tasks: [] }]);
  const removeSection = (sid) => askConfirm(t("studio.wpDeleteSection"), () => setSections((ss) => ss.filter((s) => s.id !== sid)));
  const setSectionTitle = (sid, title) => setSections((ss) => ss.map((s) => (s.id === sid ? { ...s, title } : s)));
  const addTask = (sid) => setSections((ss) => ss.map((s) => (s.id === sid ? { ...s, tasks: [...s.tasks, { id: uid(), name: "", start: "", duration: 1, end: "", actual_start: "", actual_end: "", responsible: "", predecessor: "", progress: 0, milestone: false, notes: "" }] } : s)));
  const removeTask = (sid, tid) => setSections((ss) => ss.map((s) => (s.id === sid ? { ...s, tasks: s.tasks.filter((tk) => tk.id !== tid) } : s)));
  const updateTask = (sid, tid, patch) => {
    setSections((ss) => ss.map((s) => (s.id !== sid ? s : {
      ...s,
      tasks: s.tasks.map((tk) => {
        if (tk.id !== tid) return tk;
        const n = { ...tk, ...patch };
        if ("start" in patch || "duration" in patch) {
          n.end = n.start && n.duration > 0 ? addDays(n.start, n.duration) : n.milestone && n.start ? n.start : "";
        } else if ("end" in patch && n.start && n.end) {
          n.duration = Math.max(0, diffDays(n.start, n.end));
        }
        if (n.milestone && n.start) n.end = n.start;
        return n;
      }),
    })));
  };

  const { ganttTasks, ganttLinks } = useMemo(() => {
    const tasks = [];
    const links = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    sections.forEach((s) => {
      const dated = s.tasks.filter((tk) => toDate(tk.start) && (tk.milestone || toDate(tk.end)));
      tasks.push({ id: `sec-${s.id}`, text: s.title || "—", type: "summary", open: true });
      dated.forEach((tk) => {
        const start = toDate(tk.start);
        const end = tk.milestone ? new Date(start.getTime() + 86400000) : new Date(toDate(tk.end).getTime() + 86400000);
        const done = (tk.progress ?? 0) >= 100;
        const overdue = !done && !tk.milestone && toDate(tk.end) && toDate(tk.end) < today;
        const status = tk.milestone ? "milestone" : done ? "done" : overdue ? "overdue" : tk.actual_start ? "actual" : "active";
        tasks.push({
          id: tk.id,
          text: tk.name || "—",
          parent: `sec-${s.id}`,
          start,
          end,
          progress: tk.milestone ? 0 : tk.progress || 0,
          type: tk.milestone ? "milestone" : "task",
          details: tk.responsible || "",
          status,
        });
        if (tk.predecessor && tk.predecessor !== tk.id) links.push({ id: `l-${tk.predecessor}-${tk.id}`, source: tk.predecessor, target: tk.id, type: "e2s" });
      });
    });
    return { ganttTasks: tasks, ganttLinks: links };
  }, [sections]);

  const saveMut = useMutation({
    mutationFn: async () => api.put(`/studio/work-programs/${program.id}`, { name, meta, sections }),
    onSuccess: () => { toast.success(t("studio.wpSaved")); qc.invalidateQueries({ queryKey: ["studio-work-programs"] }); qc.invalidateQueries({ queryKey: ["studio-work-program", program.id] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const baselineMut = useMutation({
    mutationFn: async () => (await api.post(`/studio/work-programs/${program.id}/baseline`)).data,
    onSuccess: (d) => { toast.success(t("studio.wpBaselineSet")); setBaseline(d.baseline); qc.invalidateQueries({ queryKey: ["studio-work-programs"] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const pickBaseline = () => (baseline ? askConfirm(t("studio.wpRebaselineConfirm"), () => baselineMut.mutate()) : baselineMut.mutate());

  const variance = (tk) => {
    const b = baseline?.tasks?.[tk.id];
    if (!b || !b.end || !tk.end) return null;
    return diffDays(b.end, tk.end);
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get(`/studio/work-programs/${program.id}/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "work-program").replace(/\s+/g, "-")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("studio.exportDone"));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const parts = [];
      if (ganttRef.current && ganttTasks.length > sections.length) parts.push(await toPng(ganttRef.current, { backgroundColor: "#ffffff", pixelRatio: 2, skipFonts: true }));
      if (tableRef.current) parts.push(await toPng(tableRef.current, { backgroundColor: "#ffffff", pixelRatio: 2, skipFonts: true }));
      const ims = [];
      for (const u of parts) { const im = new Image(); await new Promise((res, rej) => { im.onload = res; im.onerror = rej; im.src = u; }); ims.push(im); }
      if (!ims.length) throw new Error("nothing to export");
      const width = Math.max(...ims.map((i) => i.width));
      const scaled = ims.map((i) => ({ im: i, h: Math.round((i.height * width) / i.width) }));
      let logo = null;
      try { logo = new Image(); await new Promise((res, rej) => { logo.onload = res; logo.onerror = rej; logo.src = "/pruva-logo-full.png"; }); } catch { logo = null; }

      const bandPx = 150;
      const full = document.createElement("canvas");
      full.width = width;
      full.height = bandPx + scaled.reduce((s, p) => s + p.h, 0);
      const fctx = full.getContext("2d");
      fctx.fillStyle = "#ffffff";
      fctx.fillRect(0, 0, width, full.height);
      fctx.fillStyle = "#0f172a";
      fctx.fillRect(0, 0, width, bandPx);
      const textX = logo ? 170 : 24;
      if (logo) { const lh = 92; const lw = (logo.width / logo.height) * lh; fctx.drawImage(logo, 24, (bandPx - lh) / 2, lw, lh); }
      fctx.textBaseline = "middle";
      fctx.textAlign = "left";
      fctx.fillStyle = "#ffffff";
      fctx.font = "bold 42px sans-serif";
      fctx.fillText(project?.name || "", textX, 44);
      fctx.fillStyle = "#94a3b8";
      fctx.font = "26px sans-serif";
      const sub = [name, meta.contract_no ? `Contract: ${meta.contract_no}` : "", meta.revision ? `Rev: ${meta.revision}` : "", meta.rev_date || new Date().toISOString().slice(0, 10)].filter(Boolean).join("  ·  ");
      fctx.fillText(sub, textX, 96);
      fctx.font = "bold 30px sans-serif";
      fctx.fillStyle = "#38bdf8";
      fctx.textAlign = "right";
      fctx.fillText(t("studio.wpProgrammeTag"), width - 24, 62);
      let yy = bandPx;
      scaled.forEach((p) => { fctx.drawImage(p.im, 0, yy, width, p.h); yy += p.h; });

      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const margin = 8;
      const w = 297 - 2 * margin;
      const maxH = 210 - 2 * margin;
      const scale = w / full.width;
      const sliceH = Math.max(80, Math.floor(maxH / scale));
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      let y = 0;
      let first = true;
      while (y < full.height) {
        const hSrc = Math.min(sliceH, full.height - y);
        canvas.width = full.width;
        canvas.height = hSrc;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(full, 0, y, full.width, hSrc, 0, 0, full.width, hSrc);
        if (!first) pdf.addPage();
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, margin, w, hSrc * scale);
        y += hSrc;
        first = false;
      }
      pdf.save(`work-program-${(project?.name || "project").replace(/\s+/g, "-")}.pdf`);
      toast.success(t("studio.exportDone"));
    } catch (e) { toast.error(fmtErr(e)); } finally { setExporting(false); }
  };

  const scales = [
    { unit: "month", step: 1, format: "%F %Y" },
    { unit: "day", step: 1, format: "%j" },
  ];

  return (
    <div className="space-y-4" data-testid="wp-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onBack} data-testid="wp-back"><ArrowLeft className="mr-1 h-4 w-4" /> {t("studio.backToList")}</Button>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="w-72 font-display font-bold" data-testid="wp-name-edit" />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {baseline && (
            <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600" data-testid="wp-baseline-badge">
              {t("studio.wpBaselineInfo", { rev: baseline.revision || "—", date: (baseline.created_at || "").slice(0, 10) })}
            </span>
          )}
          <Button size="sm" variant="outline" onClick={pickBaseline} disabled={baselineMut.isPending} data-testid="wp-set-baseline"><Lock className="mr-1 h-4 w-4" /> {baseline ? t("studio.wpRebaseline") : t("studio.wpSetBaseline")}</Button>
          <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="wp-export-xlsx"><FileSpreadsheet className="mr-1 h-4 w-4" /> {t("studio.wpExportXlsx")}</Button>
          <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting} data-testid="wp-export-pdf"><FileImage className="mr-1 h-4 w-4" /> {t("studio.wpExportPdf")}</Button>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !name.trim()} data-testid="wp-save"><Save className="mr-1 h-4 w-4" /> {t("common.save")}</Button>
        </div>
      </div>

      <div ref={tableRef} className="space-y-4 bg-white">
        <div className="grid grid-cols-2 gap-3 rounded-lg border bg-white p-4 md:grid-cols-5" data-testid="wp-meta">
          <div className="space-y-1"><Label className="text-xs">{t("studio.wpContract")}</Label><Input value={meta.contract_no} onChange={(e) => setMeta({ ...meta, contract_no: e.target.value })} data-testid="wp-meta-contract" /></div>
          <div className="space-y-1"><Label className="text-xs">{t("studio.wpRevision")}</Label><Input value={meta.revision} onChange={(e) => setMeta({ ...meta, revision: e.target.value })} data-testid="wp-meta-revision" /></div>
          <div className="space-y-1"><Label className="text-xs">{t("studio.wpRevDate")}</Label><Input type="date" value={meta.rev_date || ""} onChange={(e) => setMeta({ ...meta, rev_date: e.target.value })} data-testid="wp-meta-revdate" /></div>
          <div className="space-y-1"><Label className="text-xs">{t("studio.wpPreparedBy")}</Label><Input value={meta.prepared_by} onChange={(e) => setMeta({ ...meta, prepared_by: e.target.value })} data-testid="wp-meta-prepared" /></div>
          <div className="space-y-1"><Label className="text-xs">{t("studio.wpApprovedBy")}</Label><Input value={meta.approved_by} onChange={(e) => setMeta({ ...meta, approved_by: e.target.value })} data-testid="wp-meta-approved" /></div>
        </div>

        {sections.map((s) => (
          <div key={s.id} className="rounded-lg border bg-white" data-testid={`wp-section-${s.id}`}>
            <div className="flex items-center gap-2 border-b bg-slate-50 px-3 py-2">
              <Input value={s.title} onChange={(e) => setSectionTitle(s.id, e.target.value)} placeholder={t("studio.wpSectionPh")} className="h-8 max-w-md font-semibold" data-testid={`wp-section-title-${s.id}`} />
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="outline" onClick={() => addTask(s.id)} data-testid={`wp-add-task-${s.id}`}><Plus className="mr-1 h-3.5 w-3.5" /> {t("studio.wpAddTask")}</Button>
                <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => removeSection(s.id)} data-testid={`wp-section-del-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="min-w-52 px-3 py-2">{t("studio.wpColTask")}</th>
                    <th className="w-32 px-2 py-2">{t("studio.wpColStart")}</th>
                    <th className="w-20 px-2 py-2">{t("studio.wpColDur")}</th>
                    <th className="w-32 px-2 py-2">{t("studio.wpColEnd")}</th>
                    <th className="w-36 px-2 py-2">{t("studio.wpColResp")}</th>
                    <th className="w-44 px-2 py-2">{t("studio.wpColPred")}</th>
                    <th className="w-20 px-2 py-2">{t("studio.wpColProg")}</th>
                    <th className="w-32 px-2 py-2">{t("studio.wpColActStart")}</th>
                    <th className="w-32 px-2 py-2">{t("studio.wpColActEnd")}</th>
                    {baseline && <th className="w-16 px-2 py-2 text-right">{t("studio.wpColVariance")}</th>}
                    <th className="w-20 px-2 py-2 text-right">{t("studio.wpColActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.tasks.map((tk) => (
                    <tr key={tk.id} className="border-b last:border-0" data-testid={`wp-task-row-${tk.id}`}>
                      <td className="px-3 py-1.5"><Input value={tk.name} onChange={(e) => updateTask(s.id, tk.id, { name: e.target.value })} className="h-8" data-testid={`wp-task-name-${tk.id}`} /></td>
                      <td className="px-2 py-1.5"><Input type="date" value={tk.start || ""} onChange={(e) => updateTask(s.id, tk.id, { start: e.target.value })} className="h-8" data-testid={`wp-task-start-${tk.id}`} /></td>
                      <td className="px-2 py-1.5"><Input type="number" min="0" value={tk.duration} onChange={(e) => updateTask(s.id, tk.id, { duration: Number(e.target.value) || 0 })} className="h-8 font-mono" data-testid={`wp-task-dur-${tk.id}`} /></td>
                      <td className="px-2 py-1.5"><Input type="date" value={tk.end || ""} onChange={(e) => updateTask(s.id, tk.id, { end: e.target.value })} className="h-8" data-testid={`wp-task-end-${tk.id}`} /></td>
                      <td className="px-2 py-1.5"><Input value={tk.responsible} onChange={(e) => updateTask(s.id, tk.id, { responsible: e.target.value })} className="h-8" data-testid={`wp-task-resp-${tk.id}`} /></td>
                      <td className="px-2 py-1.5">
                        <Select value={tk.predecessor || "__none__"} onValueChange={(v) => updateTask(s.id, tk.id, { predecessor: v === "__none__" ? "" : v })}>
                          <SelectTrigger className="h-8" data-testid={`wp-task-pred-${tk.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">{t("studio.wpNoPred")}</SelectItem>
                            {allTasks.filter((o) => o.id !== tk.id).map((o) => (
                              <SelectItem key={o.id} value={o.id} data-testid={`wp-pred-opt-${tk.id}-${o.id}`}>{o.sectionTitle ? `${o.sectionTitle} · ` : ""}{o.name || "—"}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1.5"><Input type="number" min="0" max="100" value={tk.progress} onChange={(e) => updateTask(s.id, tk.id, { progress: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} className="h-8 font-mono" data-testid={`wp-task-prog-${tk.id}`} /></td>
                      <td className="px-2 py-1.5"><Input type="date" value={tk.actual_start || ""} onChange={(e) => updateTask(s.id, tk.id, { actual_start: e.target.value })} className="h-8" data-testid={`wp-task-astart-${tk.id}`} /></td>
                      <td className="px-2 py-1.5"><Input type="date" value={tk.actual_end || ""} onChange={(e) => updateTask(s.id, tk.id, { actual_end: e.target.value })} className="h-8" data-testid={`wp-task-aend-${tk.id}`} /></td>
                      {baseline && (
                        <td className="px-2 py-1.5 text-right font-mono text-xs" data-testid={`wp-task-var-${tk.id}`}>
                          {(() => { const v = variance(tk); if (v === null) return <span className="text-slate-300">—</span>; const cls = v > 0 ? "font-semibold text-rose-600" : v < 0 ? "font-semibold text-emerald-600" : "text-slate-400"; return <span className={cls}>{v > 0 ? `+${v}` : v}</span>; })()}
                        </td>
                      )}
                      <td className="px-2 py-1.5">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" title={t("studio.wpMilestone")} className={tk.milestone ? "text-amber-600" : "text-slate-300"} onClick={() => updateTask(s.id, tk.id, { milestone: !tk.milestone })} data-testid={`wp-milestone-${tk.id}`}><Flag className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => removeTask(s.id, tk.id)} data-testid={`wp-task-del-${tk.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {s.tasks.length === 0 && <tr><td colSpan={baseline ? 11 : 10} className="px-3 py-4 text-center text-xs text-slate-400">{t("studio.wpNoTasks")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <Button variant="outline" onClick={addSection} className="w-full border-dashed" data-testid="wp-add-section"><Plus className="mr-1.5 h-4 w-4" /> {t("studio.wpAddSection")}</Button>
      </div>

      <div ref={ganttWrapRef} className="rounded-lg border bg-white" data-testid="wp-gantt">
        <div className="flex items-center gap-3 px-3 pt-2 text-xs text-slate-500" data-testid="wp-gantt-legend">
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-sky-600" />{t("studio.legendActive")}</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-500" />{t("studio.wpLegendActual")}</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-600" />{t("studio.legendDone")}</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-rose-600" />{t("studio.legendOverdue")}</span>
        </div>
        <div ref={ganttRef} style={{ height: 420 }} className="bg-white">
          {!ganttReady ? (
            <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
          ) : ganttTasks.every((gt) => gt.type === "summary") ? (
            <p className="py-12 text-center text-sm text-slate-400" data-testid="wp-gantt-empty">{t("studio.wpGanttEmpty")}</p>
          ) : (
            <GanttBoundary>
              <Willow>
                <Gantt
                  tasks={ganttTasks}
                  links={ganttLinks}
                  scales={scales}
                  readonly
                  taskTemplate={({ data }) => (
                    data.type === "milestone" ? (
                      <div className="wp-gms" title={data.text} data-testid={`wp-gms-${data.id}`} />
                    ) : (
                      <div className={`wp-gbar wp-gbar-${data.status || "active"}`} data-testid={`wp-gbar-${data.id}`}>
                        <div className="wp-gbar-fill" style={{ width: `${data.progress || 0}%` }} />
                      </div>
                    )
                  )}
                  columns={[
                    { id: "text", header: t("studio.wpColTask"), width: 240 },
                    { id: "details", header: t("studio.wpColResp"), width: 140 },
                  ]}
                />
              </Willow>
            </GanttBoundary>
          )}
        </div>
      </div>
      <style>{`
        .wp-gbar { position: relative; height: 100%; width: 100%; border-radius: 4px; overflow: hidden; }
        .wp-gbar-active { background: #0284c7; }
        .wp-gbar-actual { background: #f59e0b; }
        .wp-gbar-done { background: #059669; }
        .wp-gbar-overdue { background: #e11d48; }
        .wp-gbar-fill { position: absolute; top: 0; bottom: 0; left: 0; background: rgba(0, 0, 0, 0.28); }
        .wp-gms { width: 13px; height: 13px; margin: auto; background: #d97706; transform: rotate(45deg); border-radius: 2px; }
      `}</style>
      {confirmDialog}
    </div>
  );
}

export default function StudioWorkProgram() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedId } = useProject();
  const qc = useQueryClient();
  const { askConfirm, confirmDialog } = useConfirm();
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const { data: programs = [], isLoading } = useQuery({
    queryKey: ["studio-work-programs", selectedId],
    queryFn: async () => (await api.get("/studio/work-programs", { params: { project_id: selectedId } })).data,
    enabled: !!selectedId,
  });
  const { data: openProgram } = useQuery({
    queryKey: ["studio-work-program", openId],
    queryFn: async () => (await api.get(`/studio/work-programs/${openId}`)).data,
    enabled: !!openId,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/studio/work-programs", { project_id: selectedId, name: newName })).data,
    onSuccess: (d) => { toast.success(t("studio.wpCreated")); setCreateOpen(false); setNewName(""); qc.invalidateQueries({ queryKey: ["studio-work-programs"] }); setOpenId(d.id); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/studio/work-programs/${id}`),
    onSuccess: () => { toast.success(t("studio.wpDeleted")); qc.invalidateQueries({ queryKey: ["studio-work-programs"] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  if (!isDriveEmployee(user)) return <Navigate to="/" replace />;

  if (openId) {
    return openProgram ? (
      <WpEditor key={openProgram.id} program={openProgram} onBack={() => setOpenId(null)} />
    ) : (
      <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
    );
  }

  return (
    <div className="space-y-6" data-testid="studio-work-program-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{t("studio.wpTitle")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("studio.wpSub")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="wp-new">
          <Plus className="mr-1.5 h-4 w-4" /> {t("studio.wpNew")}
        </Button>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
      ) : programs.length === 0 ? (
        <p className="rounded-lg border border-dashed py-12 text-center text-sm text-slate-400" data-testid="wp-empty">{t("studio.wpEmpty")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {programs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-lg border bg-white p-4" data-testid={`wp-card-${d.id}`}>
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setOpenId(d.id)} data-testid={`wp-open-${d.id}`}>
                <div className="truncate text-sm font-semibold text-slate-900">{d.name}</div>
                <div className="mt-0.5 font-mono text-[10px] text-slate-400">{[d.meta?.revision ? `Rev ${d.meta.revision}` : "", (d.updated_at || "").slice(0, 16).replace("T", " ")].filter(Boolean).join(" · ")}</div>
              </button>
              <Button variant="ghost" size="icon" className="text-rose-600" title={t("common.delete")} onClick={() => askConfirm(t("studio.wpDeleteConfirm", { name: d.name }), () => deleteMut.mutate(d.id))} data-testid={`wp-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="wp-create-dialog">
          <DialogHeader><DialogTitle>{t("studio.wpNew")}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("studio.wpNamePh")}</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="wp-name-input" />
          </div>
          <DialogFooter>
            <Button onClick={() => createMut.mutate()} disabled={!newName.trim() || createMut.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="wp-create-submit">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}
